import React, { useRef } from 'react';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { IconCircleButton } from './IconCircleButton';
import { AnimatedTitle } from './AnimatedTitle';
import { Reveal } from './Reveal';
import { imagenes } from '../data/site';
import type { Servicio, ServicioVariante } from '../data/servicios';
interface Estilo {
  section: string;
  /** Color de la sección anterior, para difuminar la transición. */
  fundidoDesde: string;
  fondo: React.ReactNode;
  pregunta: string;
  titulo: string;
  texto: string;
  ctaTono: 'solido' | 'outline';
  rotacion: string;
  marcoImagen: string;
}
const estilos: Record<ServicioVariante, Estilo> = {
  roja: {
    section: 'grain bg-gradient-to-br from-[#B8391A] to-[#D94E1F]',
    fundidoDesde: '#0D0D0D',
    fondo: <img src={imagenes.rayosRojos} alt="" aria-hidden="true" className="h-full w-full object-cover opacity-45 mix-blend-screen" data-id="element-83" data-mp="2z549kph9ovfr" />,
    pregunta: 'text-white',
    titulo: 'text-[#8B1E1E]',
    texto: 'text-white/90',
    ctaTono: 'solido',
    rotacion: 'rotate-2',
    marcoImagen: 'border-8 border-white shadow-2xl shadow-black/50'
  },
  blanca: {
    section: 'bg-[#EFEDF1]',
    fundidoDesde: '#C9441D',
    fondo: <img src={imagenes.arcoPiedra} alt="" aria-hidden="true" className="ml-auto hidden h-full w-3/5 object-cover opacity-30 lg:block [mask-image:linear-gradient(to_right,transparent,black_35%)]" data-id="element-84" data-mp="2i4zkrsf41zz7" />,
    pregunta: 'text-black',
    titulo: 'text-black',
    texto: 'text-neutral-700',
    ctaTono: 'outline',
    rotacion: '-rotate-2',
    marcoImagen: 'border-8 border-white shadow-xl shadow-black/20'
  },
  negra: {
    section: 'bg-[#2A0B10]',
    fundidoDesde: '#EFEDF1',
    fondo: <>
        <img src={imagenes.negraFondo} alt="" aria-hidden="true" className="h-full w-full object-cover opacity-80" data-id="element-85" data-mp="3aqst0hj6e6td" />
        <div className="absolute inset-0 bg-[#1A060A]/45" aria-hidden="true" data-id="element-86" data-mp="23fbgb2lraf5o" />
      </>,
    pregunta: 'text-[#F1D9D9]',
    titulo: 'text-[#0D0D0D] [text-shadow:0_0_22px_rgba(226,150,150,0.85)]',
    texto: 'text-white/75',
    ctaTono: 'solido',
    rotacion: 'rotate-2',
    marcoImagen: 'border-8 border-[#0D0D0D] shadow-2xl shadow-black/70'
  }
};
export function ServicioSection({
  servicio
}: {
  servicio: Servicio;
}) {
  const estilo = estilos[servicio.variante];
  const reduce = useReducedMotion();
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const y = useTransform(scrollYProgress, [0, 1], ['-9%', '9%']);
  const imagenPrimero = servicio.ladoImagen === 'izquierda';
  return <section ref={sectionRef} id={servicio.id} className={`relative isolate w-full overflow-hidden py-24 lg:py-32 ${estilo.section}`} data-id="element-87" data-mp="111473myft5qe">
      <motion.div aria-hidden="true" style={reduce ? undefined : {
      y
    }} className="absolute inset-x-0 -top-[10%] h-[120%]" data-id="element-88" data-mp="15y4hga2bkunb">
        {estilo.fondo}
      </motion.div>

      {/* Fundido con la sección anterior */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-28 lg:h-40" style={{
      backgroundImage: `linear-gradient(to bottom, ${estilo.fundidoDesde}, transparent)`
    }} data-id="element-89" data-mp="ohpff45jwwvx" />

      <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-6 lg:grid-cols-2 lg:gap-16 lg:px-12" data-id="element-90" data-mp="fj55are23vjb">
        <div className={`text-center lg:text-left ${imagenPrimero ? 'lg:order-2' : ''}`} data-id="element-91" data-mp="aor4g9gmkw79">
          <Reveal data-id="element-92" data-mp="5s0n7it33uk9">
            <p className={`font-serif text-xl leading-snug sm:text-2xl ${estilo.pregunta}`} data-id="element-93" data-mp="5pejxvd8817u">
              {servicio.pregunta}
            </p>
          </Reveal>
          <AnimatedTitle text={servicio.titulo} className={`mt-4 font-serif text-4xl leading-[1.05] sm:text-5xl lg:text-6xl ${estilo.titulo}`} data-id="element-94" data-mp="3svxbncl0qrsr" />
          <Reveal delay={0.1} data-id="element-95" data-mp="1rva9q248to8m">
            <p className={`mx-auto mt-6 max-w-xl text-base leading-relaxed sm:text-lg lg:mx-0 ${estilo.texto}`} data-id="element-96" data-mp="1rv0th6902vg4">
              {servicio.texto}
            </p>
          </Reveal>
          <Reveal delay={0.16} data-id="element-97" data-mp="1i45xvb9v6ji0">
            <div className="mt-9 flex flex-wrap justify-center gap-5 lg:justify-start" data-id="element-98" data-mp="1n391cra74hnv">
              <IconCircleButton accion={servicio.cta.tipo} tono={estilo.ctaTono} tamano="lg" data-id="element-99" data-mp="18687oi8g2cmr" />
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.08} className={imagenPrimero ? 'lg:order-1' : ''} data-id="element-100" data-mp="1d6e7ilovb5fx">
          <img src={servicio.imagen.src} alt={servicio.imagen.alt} loading="lazy" className={`${estilo.rotacion} ${estilo.marcoImagen} aspect-[4/3] w-full rounded-sm object-cover`} data-id="element-101" data-mp="z63a0zt1ysls" />
        </Reveal>
      </div>
    </section>;
}